<template>
  <Teleport to="#page-wrapper" :disabled="!teleport">
    <div ref="galleryEl" class="pt-gallery inline-gallery has-fullscreen">
      <PuFlickity
        ref="ptflickity"
        :options="flickityOptions"
        @init="initGallery"
      >
        <div
          v-for="(photo, index) in photos"
          :key="'photo-' + index"
          class="pt-carousel-cell carousel-cell"
        >
          <img
            loading="lazy"
            :src="photo.url.galleryMedium"
            :srcset="
              $isDesktop && isFullScreen
                ? `/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=720,h=540${photo.url.masterImage} 720w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1080,h=810${photo.url.masterImage} 1080w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1800,h=1350${photo.url.masterImage} 1800w`
                : `/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=720,h=540${photo.url.masterImage} 720w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1080,h=810${photo.url.masterImage} 1080w`
            "
            :data-caption="
              $i18n.locale.split('-')[0] === 'en'
                ? capitalize(photo.caption)
                : capitalize(photo.titleAttribution)
            "
            :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
          />
        </div>
        <i class="photo-count photo-count-fs"
          >{{ currentImage }}/{{ photos.length }}</i
        >
        <p class="caption">
          <!-- image caption written in here -->
        </p>
      </PuFlickity>
    </div>
  </Teleport>
</template>

<script lang="ts">
import type { Route } from 'vue-router'
import { ignoreTouchesWhenSwiping } from '~/utility/flickity'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { capitalize } from '~/utility/text'
import type PuFlickity from '../../../components/PuFlickity.vue'
import { PitchTypePhoto } from '../../campsite-details/types'

@Component({})
class PitchTypeGalleryModal extends Vue {
  declare $refs: {
    ptflickity: InstanceType<typeof PuFlickity>
    galleryEl: HTMLElement
  }

  @Prop({ required: true })
  photos: PitchTypePhoto[]

  @Prop({ required: true })
  fullscreenModalHash: string

  @Prop({ default: false })
  teleport: boolean

  currentImage: number = 1
  isFullScreen: any = false
  gallery: any = null
  caption: any = null
  selectedImage: any = null
  exitFullscreenButton: HTMLElement | null = null
  flickityInstance: any = null

  get flickityOptions() {
    return {
      initialIndex: this.currentImage - 1,
      dragThreshold: 20,
      pageDots: false,
      wrapAround: true,
      lazyLoad: 1,
      cellSelector: '.pt-carousel-cell',
      selectedAttraction: 0.2,
      friction: 0.8,
      fullscreen: true,
      prevNextButtons: this.$isDesktop,
      draggable: !this.$isDesktop,
    }
  }

  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- Flickity instance is untyped */
  initGallery(flickityInstance: any) {
    this.flickityInstance = flickityInstance
    this.gallery = this.$refs.galleryEl
    this.caption = this.gallery.querySelector('.caption')
    this.initHandlers()
    if (this.$route.hash === `#${this.fullscreenModalHash}`) {
      this.flickityInstance?.viewFullscreen()
    }
    this.exitFullscreenButton =
      this.flickityInstance?.exitFullscreenButton?.element
  }

  initHandlers() {
    const f = this.$refs.ptflickity
    if (f) {
      f.on('change', () => this.updateSlideStatus())
      f.on('select', () => this.updateCaption())
      f.on(
        'fullscreenChange',
        async (fullscreenStatus: boolean) =>
          await this.fullscreenChange(fullscreenStatus),
      )
    }
    ignoreTouchesWhenSwiping(this.gallery)
  }

  @Watch('$route')
  routeChange(to: Route, from?: Route) {
    if (this.$.isUnmounted) return

    if (to.path === from?.path) {
      to.meta!.scroll = false
    }
    if (to.hash === `#${this.fullscreenModalHash}`) {
      this.flickityInstance?.viewFullscreen?.()
    } else {
      this.flickityInstance?.exitFullscreen?.()
    }
  }

  async closeFullscreen() {
    if (this.$routerClient.getReferrer()) {
      this.$router.back()
    } else {
      await this.$router.push({
        query: this.$router.currentRoute.value.query,
      })
    }
  }

  async fullscreenChange(fullscreenStatus: boolean) {
    if (this.$.isUnmounted) return

    this.isFullScreen = fullscreenStatus
    await this.$nextTick()

    if (this.$.isUnmounted) return

    this.reactivateFlickity()
    this.updateCaption()
    this.updateSlideStatus()

    if (!this.$.isUnmounted) {
      if (this.isFullScreen) {
        this.gallery.setAttribute('gallery-open', 'true')
        this.exitFullscreenButton?.addEventListener('click', () => {
          void this.closeFullscreen()
        })
      } else {
        this.gallery.removeAttribute('gallery-open')
      }
    }
  }
  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

  reactivateFlickity() {
    if (this.isFullScreen && !this.$.isUnmounted) {
      document.documentElement.classList.add('is-flickity-fullscreen')
    }
  }

  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Flickity ref and gallery DOM are untyped */
  updateSlideStatus() {
    if (this.$.isUnmounted || !this.$refs.ptflickity) return
    this.currentImage = this.$refs.ptflickity.selectedIndex() + 1
  }

  updateCaption() {
    if (this.$.isUnmounted || !this.isFullScreen || !this.gallery) return
    this.selectedImage = this.gallery.querySelector(
      '.pt-carousel-cell.is-selected img',
    )
    if (this.selectedImage) {
      this.caption.textContent = this.selectedImage.getAttribute('data-caption')
    }
  }
  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */

  capitalize(value: string) {
    return capitalize(value)
  }

  photoCaptionAltFormatter(caption) {
    return photoCaptionAltFormatter(caption)
  }

  @Watch('isFullScreen')
  changeBodyAttribute(value: boolean) {
    if (this.$.isUnmounted) return
    value
      ? document.body.setAttribute('data-flickity-fullscreen', 'true')
      : document.body.removeAttribute('data-flickity-fullscreen')
  }

  beforeUnmount() {
    if (this && document && document.body) {
      document.body.removeAttribute('data-flickity-fullscreen')
    }
    if (this && document && document.documentElement) {
      document.documentElement.classList.remove('is-flickity-fullscreen')
    }
  }
}

export default toNative(PitchTypeGalleryModal)
</script>

<style>
@import url('~/assets/css/flickity-custom.css');
</style>

<style scoped>
.pt-gallery {
  /* move offscreen until needed */
  position: absolute;
  left: -100vw;
}
</style>
